import React, { useCallback, useMemo, useState } from 'react';
import placeholder from 'components/Dashboard/Store/Products/images/placeholder.png';
import { Checkbox, SelectField, TextField, Typography } from 'ui-kit';
import { Row, Col } from 'antd';
import EmptyData from 'components/EmptyData';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ImportedStore } from 'api/types/store';

import './ImportStoresTable.scss';
import { IStoreToSync } from 'api/store_sdk';
import Stack from 'ui-kit/Stack';
import _ from 'lodash';
import { EImportStoreActionType, IMPORT_STORE_ACTION_OPTIONS } from 'components/Partners/constants';
import ConfirmModal from 'components/ConfirmModal';

type Props = {
  listings: ImportedStore[];
  storesToSync: Array<IStoreToSync>;
};

const ImportStoresTable = ({ listings, storesToSync }: Props) => {
  // State to manage the confirmation modal for disconnecting stores when the action is updated from 'sync' to 'disconnect' or 'create
  const [disconnectConfirmationIsOpen, setDisconnectConfirmationIsOpen] = useState(false);
  const [selectedListingIndex, setSelectedListingIndex] = useState<number | null>(null);
  const [selectedStoreAction, setSelectedStoreAction] = useState<EImportStoreActionType | null>(
    null,
  );
  const [updatedRows, setUpdatedRows] = useState<number[]>([]);

  const { control, watch, setValue } = useFormContext();
  const listingsWatch = useWatch({ control, name: 'listings' });

  const handleUpdateRow = useCallback(
    (index: number) => {
      setUpdatedRows((prev) => {
        const updated = [...prev];
        if (!updated.includes(index)) {
          updated.push(index);
        }
        return updated;
      });
    },
    [updatedRows],
  );

  const sortedStoresToSync = useMemo(() => {
    return (
      storesToSync?.sort((a, b) => {
        if (a.store_name < b.store_name) {
          return -1;
        }
        if (a.store_name > b.store_name) {
          return 1;
        }
        return 0;
      }) || []
    );
  }, [storesToSync]);

  const storesToSyncOptions = useMemo(() => {
    // Currently we return only stores that are linked with the particular integration
    return sortedStoresToSync.map((store) => {
      // If the store is already linked with the integration, we should disable it
      const storeShouldBeDisabled = !_.isUndefined(_.find(listingsWatch, ['storeId', store.id]));

      return {
        label: `${store.store_name}${storeShouldBeDisabled ? ' (Already Connected)' : ''}`,
        value: store.id,
        disabled: storeShouldBeDisabled,
      };
    });
  }, [sortedStoresToSync, listingsWatch]);

  const selectAll = useCallback(
    (event) => {
      const checked = event.target.checked;
      const hasExistingImportSource = listingsWatch.some(
        (listing) => listing.existingImportSource && listing.existingImportSource.storeId,
      );

      if (checked && hasExistingImportSource) {
        alert(
          'This will disconnect stores that are already linked with the integration. Are you sure?',
        );
      }

      listingsWatch.forEach((_, index) => {
        if (checked) {
          handleUpdateRow(index);
        } else {
          setUpdatedRows([]);
        }

        // If the checkbox is checked, set the action to 'create'
        // If the checkbox is unchecked, set the default action - to 'sync' if the store is already linked or 'disconnect' if not
        setValue(
          `listings.${index}.action`,
          checked
            ? EImportStoreActionType.CREATE
            : listings[index].existingImportSource
            ? EImportStoreActionType.SYNC
            : EImportStoreActionType.DISCONNECT,
        );
      });
    },
    [listingsWatch, setValue, listings],
  );

  const isAllSelected = useMemo(
    () =>
      listingsWatch?.every((listing) => listing.action === EImportStoreActionType.CREATE) || false,
    [listingsWatch],
  );

  const handleChangeAction = useCallback(
    ({
      index,
      newAction,
      showConfirmation = true,
    }: {
      index: number;
      newAction: EImportStoreActionType | number;
      showConfirmation?: boolean;
    }) => {
      const listing = listings[index];

      // If the current listing when the load the page is synced and change the action from `sync`, we should show a confirmation modal
      if (
        showConfirmation &&
        newAction !== EImportStoreActionType.SYNC &&
        listing.existingImportSource
      ) {
        setSelectedStoreAction(newAction as EImportStoreActionType);
        setDisconnectConfirmationIsOpen(true);
        setSelectedListingIndex(index);
      } else {
        // If the action is no 'sync;, we should be sure that the storeId will be reset to null
        if (newAction !== EImportStoreActionType.SYNC) {
          setValue(`listings.${index}.storeId`, null);
        } else {
          // If the listing is already synced, we should set the storeId to the existing one when the user select again 'sync'
          const storeShouldBeDisabled = !_.isUndefined(
            _.find(listingsWatch, ['storeId', listing.existingImportSource?.storeId]),
          );

          if (!storeShouldBeDisabled) {
            setValue(`listings.${index}.storeId`, listing.existingImportSource?.storeId);
          }
        }

        setValue(`listings.${index}.action`, newAction);
        setSelectedStoreAction(null);
        setSelectedListingIndex(null);
        setDisconnectConfirmationIsOpen(false);
        handleUpdateRow(index);
      }
    },
    [listings, listingsWatch, setValue, handleUpdateRow],
  );

  const handleChangeStoreId = useCallback(
    ({ value, index }: { value: string | number; index: number }) => {
      setValue(`listings.${index}.storeId`, value);
      handleUpdateRow(index);
    },
    [handleUpdateRow],
  );

  return (
    <>
      <ConfirmModal
        visible={disconnectConfirmationIsOpen}
        confirmBtnType="primary"
        confirmText="Yes"
        cancelText="Cancel"
        onConfirm={() =>
          handleChangeAction({
            index: selectedListingIndex,
            newAction: selectedStoreAction as EImportStoreActionType,
            showConfirmation: false,
          })
        }
        onCancel={() => {
          setDisconnectConfirmationIsOpen(false);
        }}
        destroyOnClose
      >
        This will disconnect the store with current listing from the integration. Are you sure you
        want to proceed?
      </ConfirmModal>
      <div>
        <Row className="ImportStores_table_header" gutter={16}>
          <Col span={2}>Photo</Col>
          <Col span={12}>
            <Stack flexDirection="column">
              Action
              <Checkbox
                id="all"
                className="ImportStores_table_checkbox"
                onChange={selectAll}
                checked={isAllSelected}
              >
                Create new stores
              </Checkbox>
            </Stack>
          </Col>

          <Col span={10}>Listing details</Col>
        </Row>
        <div className="ImportStores_table_contents">
          {!listings?.length ? (
            <EmptyData label="No properties found" />
          ) : (
            <form className="ImportStores_form">
              {listings?.map((property, index) => {
                const storeId = useWatch({
                  control,
                  name: `listings.${index}.storeId`,
                });

                const action = watch(
                  `listings.${index}.action`,
                  property.existingImportSource
                    ? EImportStoreActionType.SYNC
                    : EImportStoreActionType.DISCONNECT,
                );

                const isSynced = !_.isNull(storeId) && action === EImportStoreActionType.SYNC;

                return (
                  <Row
                    key={property.externalId}
                    gutter={16}
                    className={`ImportStores_table_row ${
                      updatedRows.includes(index) ? 'updated' : ''
                    }`}
                  >
                    <Col span={2}>
                      <Controller
                        control={control}
                        name={`listings.${index}.image`}
                        render={() => (
                          <img
                            src={property.image || placeholder}
                            alt="rental-photo"
                            width={80}
                            height={60}
                            style={{
                              maxWidth: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        )}
                      />
                    </Col>
                    <Col span={12}>
                      <Stack
                        flexDirection="column"
                        style={{ marginTop: '10px', marginBottom: '10px', width: '100%' }}
                      >
                        <Controller
                          control={control}
                          name={`listings.${index}.action`}
                          render={({ field: { value, onBlur }, fieldState: { error } }) => (
                            <SelectField
                              fullWidth
                              options={IMPORT_STORE_ACTION_OPTIONS}
                              value={value}
                              onChange={(value) =>
                                handleChangeAction({
                                  index,
                                  newAction: value as EImportStoreActionType,
                                })
                              }
                              onBlur={onBlur}
                              error={!!error}
                              errorMessage={error?.message}
                            />
                          )}
                        />
                        {action === EImportStoreActionType.SYNC && (
                          <Controller
                            control={control}
                            name={`listings.${index}.storeId`}
                            render={({ field: { value, onBlur }, fieldState: { error } }) => (
                              // TODO: make it autocomplete
                              <SelectField
                                fullWidth
                                placeholder="Sync with existing store"
                                options={storesToSyncOptions}
                                value={value}
                                defaultValue={property.existingImportSource?.storeId}
                                onChange={(value) =>
                                  handleChangeStoreId({
                                    value,
                                    index,
                                  })
                                }
                                onBlur={onBlur}
                                error={!!error}
                                errorMessage={error?.message}
                              />
                            )}
                          />
                        )}
                        {action === EImportStoreActionType.CREATE && (
                          <Controller
                            control={control}
                            name={`listings.${index}.storeName`}
                            render={({ field: { ref, ...rest }, fieldState }) => (
                              <TextField
                                required
                                fullWidth
                                inputRef={ref}
                                disabled={isSynced}
                                error={Boolean(fieldState.error)}
                                errorMessage={fieldState.error?.message}
                                {...rest}
                              />
                            )}
                          />
                        )}
                      </Stack>
                    </Col>

                    <Col span={10}>
                      <Stack flexDirection="column">
                        <Typography variant="body1">{property.storeName}</Typography>
                        <Typography variant="caption">City: {property.city}</Typography>
                        <Typography variant="caption">State: {property.state}</Typography>
                        <Typography variant="caption">Address: {property.street1}</Typography>
                        <Typography variant="caption">Zip: {property.postalCode}</Typography>
                      </Stack>
                    </Col>
                  </Row>
                );
              })}
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ImportStoresTable;
